<template>
  <div class="flex h-screen shrink-0" :style="panelStyle">
    <!-- Resize handle on the left edge of the sidebar -->
    <div
      v-show="contentVisible"
      class="shrink-0 relative cursor-col-resize group"
      style="width: 6px;"
      title="Drag to resize panel — double-click to reset"
      @mousedown.prevent="startResize"
      @dblclick="resetWidth"
    >
      <div
        class="absolute inset-y-0 left-1/2 -translate-x-1/2 transition-colors"
        style="width: 1px;"
        :class="resizing ? 'bg-sky-400' : 'bg-white/20 group-hover:bg-sky-400'"
      ></div>
    </div>
    <!-- Content area -->
    <div id="right-panel-content" v-show="contentVisible" class="flex-1 text-sm overflow-y-auto hide-scrollbar p-2 space-y-4" style="min-width: 0;">
      <div v-show="activeTab === 'pages'">
        <slot name="pages" />
      </div>
      <div v-show="activeTab === 'outline'">
        <slot name="outline" />
      </div>
      <div v-show="activeTab === 'properties'">
        <slot name="properties" />
      </div>
      <div v-if="showHistory" v-show="activeTab === 'history'" class="h-full -m-2" style="height: calc(100vh - 16px);">
        <slot name="history" />
      </div>
      <div v-show="activeTab === 'resources'" class="h-full -m-2" style="height: calc(100vh - 16px);">
        <slot name="resources" />
      </div>
      <div v-show="activeTab === 'themes'" class="h-full -m-2" style="height: calc(100vh - 16px);">
        <slot name="themes" />
      </div>
    </div>
    <!-- Vertical tab strip on right edge -->
    <div class="flex flex-col bg-white/10 border-l border-white/20 shrink-0" :style="{ width: tabStripWidth + 'px' }">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        @click="selectTab(tab.id)"
        :title="collapsed ? tab.label + ' (click to expand)' : tab.label + ' (click to collapse)'"
        class="flex items-center justify-center transition-colors"
        style="height: 72px;"
        :class="activeTab === tab.id ? 'bg-slate-500 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-white'"
      >
        <span class="text-xs font-medium select-none" style="writing-mode: vertical-rl; transform: rotate(180deg);">{{ tab.label }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import {
  useUiLayoutStore,
  clampRightPanelWidth,
  getEffectiveRightPanelWidth,
  RIGHT_PANEL_DEFAULT_WIDTH,
  RIGHT_PANEL_TAB_STRIP_WIDTH,
} from '../stores/uiLayoutStore.js';

const ALL_TABS = [
  { id: 'pages', label: 'Pages' },
  { id: 'outline', label: 'Outline' },
  { id: 'properties', label: 'Properties' },
  { id: 'history', label: 'History' },
  { id: 'resources', label: 'Resources' },
  { id: 'themes', label: 'Themes' },
];

export default {
  name: 'RightPanel',
  props: {
    showHistory: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      resizing: false,
      dragStartX: 0,
      dragStartWidth: 0,
      tabStripWidth: RIGHT_PANEL_TAB_STRIP_WIDTH,
      // Measured layout space, integers only (see getEffectiveRightPanelWidth).
      viewportWidth: 0,
      leftWidth: 0,
    };
  },
  computed: {
    uiLayout() {
      return useUiLayoutStore();
    },
    activeTab() {
      return this.uiLayout.activeRightTab;
    },
    collapsed() {
      return this.uiLayout.rightPanelCollapsed;
    },
    // The user's chosen width — persisted, never overwritten by a window resize.
    preferredWidth() {
      return this.uiLayout.rightPanelWidth;
    },
    // What is actually rendered: the preferred width capped by the space the
    // window has right now, so the tab strip is never pushed off-screen. When
    // the window grows again the preferred width comes back on its own.
    width() {
      return getEffectiveRightPanelWidth({
        preferredWidth: this.preferredWidth,
        viewportWidth: this.viewportWidth,
        leftWidth: this.leftWidth,
        tabStripWidth: this.tabStripWidth,
        collapsed: this.collapsed,
      });
    },
    tabs() {
      return this.showHistory ? ALL_TABS : ALL_TABS.filter((t) => t.id !== 'history');
    },
    // The window can be too small for any content at all; then only the tab
    // strip renders, so handle + strip can never overflow the panel's box.
    contentVisible() {
      return !this.collapsed && this.width > this.tabStripWidth;
    },
    panelStyle() {
      return { width: this.width + 'px' };
    },
  },
  methods: {
    selectTab(tab) {
      this.uiLayout.selectRightTab(tab);
    },
    startResize(event) {
      this.resizing = true;
      this.dragStartX = event.clientX;
      this.dragStartWidth = this.width;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('mousemove', this.onResizeMove);
      window.addEventListener('mouseup', this.stopResize);
    },
    onResizeMove(event) {
      // Dragging left widens the sidebar, dragging right narrows it.
      const next = clampRightPanelWidth(this.dragStartWidth - (event.clientX - this.dragStartX));
      if (next === this.width) return;
      this.uiLayout.setRightPanelWidth(next);
    },
    stopResize() {
      this.resizing = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', this.onResizeMove);
      window.removeEventListener('mouseup', this.stopResize);
    },
    resetWidth() {
      this.uiLayout.setRightPanelWidth(RIGHT_PANEL_DEFAULT_WIDTH);
    },
    // Remeasure the space this panel has to fit in. Only the app row and the
    // (fixed) left sidebar are measured — never this panel itself — so writing
    // our own width back can't feed into the next measurement.
    measureLayout() {
      const row = this.$el?.parentElement;
      const left = document.getElementById('left_sidebar');
      const viewportWidth = Math.floor(row?.clientWidth ?? 0);
      const leftWidth = Math.floor(left?.offsetWidth ?? 0);
      if (viewportWidth !== this.viewportWidth) this.viewportWidth = viewportWidth;
      if (leftWidth !== this.leftWidth) this.leftWidth = leftWidth;
    },
  },
  mounted() {
    this.measureLayout();
    // Observing the row and the left sidebar (not this panel) keeps this a
    // one-way dependency: window/left changes -> our effective width.
    if (typeof ResizeObserver !== 'undefined') {
      this._layoutObserver = new ResizeObserver(() => this.measureLayout());
      const row = this.$el?.parentElement;
      const left = document.getElementById('left_sidebar');
      if (row) this._layoutObserver.observe(row);
      if (left) this._layoutObserver.observe(left);
    } else {
      window.addEventListener('resize', this.measureLayout);
    }
  },
  beforeUnmount() {
    this.stopResize();
    this._layoutObserver?.disconnect();
    window.removeEventListener('resize', this.measureLayout);
  },
};
</script>
